<template>
  <v-autocomplete
    ref="autocomplete"
    :clearable="clearable"
    :color="$finColors.primaryDark"
    :item-disabled="itemDisabled"
    :item-text="itemName"
    :item-value="itemValue"
    :items="entries"
    :loading="isLoading"
    :no-data-text="search && search.trim().length > 0 ? noDataText : noDataTextNoSearchKeyword"
    :no-filter="!Array.isArray(resolver)"
    :placeholder="instantClearSelection ? null : prevSearch || placeholder"
    :prepend-inner-icon="searchIcon"
    :search-input.sync="search"
    :value="value"
    hide-details
    outlined
    :autofocus="autofocus"
    return-object
    @blur="blurAutocomplete"
    @focus="focusAutocomplete"
    @input="(val) => selectionChanged(val)"
  >
    <template v-if="$scopedSlots.item" #item="props">
      <!-- @slot item override the display of the autocomplete items -->
      <slot v-bind="props" name="item"/>
    </template>

    <template v-if="entries.length > 0" #append-item>
      <div v-intersect="{
            handler: onIntersect,
          }"/>
    </template>
  </v-autocomplete>
</template>

<script>
import DataFetcher from "@/components/common/DataLoader/DataFetcher"
import {debounce} from 'debounce'

export default {
  name: "finAutocomplete",
  props: {
    /** The value object */
    value: {
      type: Object
    },
    /** Array of strings or objects, Promise or Function (that returns a Promise or array), used for the search */
    resolver: {},
    /** The item value to be selected */
    itemValue: {
      type: [String, Array, Function],
      default: 'id'
    },
    /** The item name to be displayed */
    itemName: {
      type: [String, Array, Function],
      default: 'name'
    },
    /** Used to set if an item is disabled, can be string, array or function */
    itemDisabled: {},
    /** Set if the search keyword can be cleared */
    clearable: {
      type: Boolean,
      default: false
    },
    /** The placeholder to show if autocomplete doesn't have the focus */
    placeholder: {
      type: String
    },
    /** The text to show if nothing was found */
    noDataText: {
      type: String,
      default: 'Keine Daten vorhanden'
    },
    /** The text to show if no search keyword is entered */
    noDataTextNoSearchKeyword: {
      type: String,
      default: ''
    },
    /** Customize the inner search icon */
    searchIcon: {
      type: String,
      default: 'icon-search'
    },
    /** Set if the selection should be instantly cleared */
    instantClearSelection: {
      type: Boolean,
      default: false
    },
    /** If component should automatically get focus when the page loads */
    autofocus: {
      type: Boolean
    },
  },
  data() {
    return {
      search: null,
      isLoading: false,
      entries: [],
      prevSearch: null,
      prevEntries: [],
      /**
       * Is used to cancel pending promises
       */
      lastPromise: null,
      emptyOrLastResult: false,
    }
  },
  methods: {
    onIntersect(entries, observer) {
      if (entries[0].intersectionRatio >= 0.5 && !this.emptyOrLastResult) {
        this._loading()
      }
    },
    _loading() {
      this.isLoading = true
      const promise = DataFetcher.fetchData(this.resolver, {searchKeyword: this.search, from: this.entries.length})
      this.lastPromise = promise

      // Cancels promise if newer promise is faster than older promise
      return new Promise((resolve, reject) => {
        promise.then(result => {
          // Check if promise is the latest send
          if (promise === this.lastPromise) {
            this.lastPromise = null
            this.entries = this.entries.concat(result)
            this.emptyOrLastResult = result.length === 0
          }
        }).finally(() => {
          this.isLoading = false
        })
      })
    },
    _searchByKeyword() {
      this.emptyOrLastResult = false
      this.entries = []
      this._loading()
    },
    blurAutocomplete() {
      if (this.search && this.entries && this.entries.length > 0) {
        this.prevEntries = this.entries
        this.prevSearch = this.search
      }
    },
    focusAutocomplete() {
      if (this.prevSearch && this.prevSearch.length > 0) {
        this.search = this.prevSearch
        this.entries = this.prevEntries
      }
    },
    selectionChanged(val) {
      if (this.instantClearSelection) {
        this.search = null
        this.prevSearch = null
        this.prevEntries = []
        this.entries = []
        // autocomplete default = selected value is shown + can't be re-selected without clearing the search by
        // clicking the icon (clearable must be set)
        this.$refs.autocomplete.lazyValue = null
      }

      /**
       * Event triggered when changing the selection of the autocomplete.
       *
       * @event input
       * @type {object}
       */
      this.$emit('input', val)
    }
  },
  watch: {
    search: debounce(async function (searchKeyword) {
      if (searchKeyword === undefined || searchKeyword === null) {
        this.prevEntries = null
        this.prevSearch = null
        this.entries = []
        return
      }

      // skip if no searchKeyword is set
      if (!searchKeyword) {
        this.entries = []
        return
      }

      // skip if the previous search result can be used
      if (searchKeyword === this.prevSearch) {
        return
      }
      await this._searchByKeyword()
    }, 350),
  },
}
</script>

<style scoped>
::v-deep .icon-search.theme--dark {
  color: #fff !important;
}

::v-deep fieldset {
  border-color: #fff !important;
}

::v-deep .mdi-menu-down.theme--dark {
  color: #fff !important;
}
</style>
